import React, { useEffect, useRef, useState } from "react";
import { ATHLETE_HEADSHOT_SILHOUETTE_IMAGE, isFallbackHeadshotImage } from "./athleteHeadshotPlaceholder";
import { getFaceFraming } from "./athleteFaceFraming";

const ATHLETE_HEADSHOT_INTERSECTION_ROOT_MARGIN = "200px 0px";
const COMPACT_HEADSHOT_LEAGUES = new Set(["soccer", "worldcup", "worldcup1h", "worldcup2h", "cs2", "lol", "val", "cricket", "kbo", "darts"]);
export const usesCompactHeadshot = (league) => COMPACT_HEADSHOT_LEAGUES.has(String(league || "").toLowerCase());

const AthleteHeadshotImage = ({ alt, className = "", faceNormalization, height = 110, placeholderWhileLoading = false, priority = false, src }) => {
  const frameRef = useRef(null);
  const imageRef = useRef(null);
  const previousSrcRef = useRef(src);
  const [shouldLoad, setShouldLoad] = useState(priority);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const hasHeadshotSrc = !isFallbackHeadshotImage(src);

  useEffect(() => {
    const srcChanged = previousSrcRef.current !== src;
    previousSrcRef.current = src;

    if (srcChanged) {
      setImageLoaded(false);
      setImageFailed(false);
      setShouldLoad(priority);
      return;
    }

    if (priority) {
      setShouldLoad(true);
    }
  }, [priority, src]);

  useEffect(() => {
    if (!src || shouldLoad) {
      return undefined;
    }

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => (
          frameRef.current?.closest(".athlete-card")?.getBoundingClientRect().top < (entry.rootBounds?.bottom ?? window.innerHeight) &&
          entry.isIntersecting
        ))) {
          setShouldLoad(true);
        }
      },
      {
        rootMargin: ATHLETE_HEADSHOT_INTERSECTION_ROOT_MARGIN,
        threshold: 0,
      }
    );

    if (frameRef.current) {
      observer.observe(frameRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [shouldLoad, src]);

  useEffect(() => {
    if (!hasHeadshotSrc || !shouldLoad) {
      setImageLoaded(false);
      return;
    }

    if (imageRef.current?.complete && imageRef.current.naturalWidth > 0) {
      setImageLoaded(true);
    }
  }, [hasHeadshotSrc, shouldLoad, src]);

  const imagePending = !hasHeadshotSrc || !shouldLoad || !imageLoaded;
  const imageClassName = [
    className,
    "athlete-headshot__image",
    imagePending ? "athlete-headshot__image--pending" : "",
    imageLoaded ? "athlete-headshot__image--loaded" : "",
    !hasHeadshotSrc ? "athlete-headshot__image--fallback" : "",
  ].filter(Boolean).join(" ");
  const imageFetchPriority = priority ? "high" : undefined;
  const imageSrc = hasHeadshotSrc && shouldLoad ? src : undefined;
  const showSilhouette = !hasHeadshotSrc || imageFailed || (placeholderWhileLoading && !imageLoaded);
  const faceFraming = hasHeadshotSrc ? getFaceFraming(faceNormalization) : null;
  const frameClassName = [
    "athlete-headshot__frame",
    faceFraming ? "athlete-headshot__frame--face-normalized" : "",
  ].filter(Boolean).join(" ");
  const faceStyle = faceFraming ? {
    "--athlete-face-origin-x": `${faceFraming.faceCenterX * 100}%`,
    "--athlete-face-origin-y": `${faceFraming.eyeLineY * 100}%`,
    "--athlete-face-translate-x": `${(0.5 - faceFraming.faceCenterX) * 100}%`,
    "--athlete-face-translate-y": `${(faceFraming.targetEyeLine - faceFraming.eyeLineY) * 100}%`,
    "--athlete-face-scale": faceFraming.recommendedScale,
  } : undefined;

  return (
    <span className={frameClassName} data-src={!shouldLoad && hasHeadshotSrc ? src : undefined} ref={frameRef} style={faceStyle}>
      {showSilhouette && (
        <img
          alt=""
          aria-hidden="true"
          className={hasHeadshotSrc
            ? "athlete-headshot__loading-silhouette"
            : "athlete-headshot__image athlete-headshot__image--fallback"}
          height={height}
          src={ATHLETE_HEADSHOT_SILHOUETTE_IMAGE}
        />
      )}
      {imageSrc && (
        <img
          alt={alt}
          className={imageClassName}
          decoding="async"
          fetchPriority={imageFetchPriority}
          height={height}
          loading={priority ? "eager" : "lazy"}
          onError={() => {
            setImageFailed(true);
            setImageLoaded(false);
          }}
          onLoad={() => {
            setImageFailed(false);
            setImageLoaded(true);
          }}
          ref={imageRef}
          src={imageSrc}
          style={{ opacity: imageLoaded ? 1 : 0 }}
        />
      )}
    </span>
  );
};

export default AthleteHeadshotImage;
